/**
 * Libero Frontend — App Shell
 *
 * React Router with lazy-loaded pages (E53).
 * Auth check on mount via httpOnly cookie (E13).
 * Layout with dynamic sidebar (E58).
 */

import { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import EnvironmentBanner, { useEnvironmentStatus } from './components/EnvironmentBanner';

// Lazy-loaded pages — code-split (E53)
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const ProgrammePage = lazy(() => import('./pages/ProgrammePage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-yellow" />
    </div>
  );
}

function AppRoutes() {
  const { isLoading, isAuthenticated, checkAuth } = useAuthStore();
  const { isVisible: bannerVisible } = useEnvironmentStatus();
  const hasChecked = useRef(false);

  useEffect(() => {
    // Nur einmal prüfen, nicht bei jedem Re-Render
    if (!hasChecked.current) {
      hasChecked.current = true;
      checkAuth();
    }
  }, [checkAuth]);

  // Auf /login NICHT warten bis Auth-Check fertig ist
  const isLoginPage = window.location.pathname.startsWith('/login') ||
    window.location.pathname.startsWith('/forgot-password') ||
    window.location.pathname.startsWith('/reset-password') ||
    window.location.pathname.startsWith('/invite');

  if (isLoading && !isLoginPage) {
    return <LoadingSpinner />;
  }

  return (
    <div className={bannerVisible ? 'pt-6' : ''}>
      <Routes>
      {/* Public routes — no layout */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />

      {/* Protected routes — with Layout (Sidebar + Content) */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="category/:categoryName" element={<CategoryPage />} />
        <Route path="programme/:programmeId/*" element={<ProgrammePage />} />
        <Route path="history" element={<HistoryPage />} />
        <Route
          path="settings/*"
          element={
            <ProtectedRoute
              anyOfPermissions={[
                'manage:users',
                'manage:integrations',
                'manage:schedules',
                'manage:system',
                'manage:access',
                'manage:roles',
              ]}
            >
              <SettingsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </div>
  );
}

export default function App() {
  return (
    <>
      <EnvironmentBanner />
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner />}>
          <AppRoutes />
        </Suspense>
      </BrowserRouter>
    </>
  );
}
